import React, { useEffect, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { PropTypes } from "prop-types";
import { useI18n } from "../../../../i18n/useI18n";
import { am4core, am4charts } from "../../../../loaders/amchartsLoader";
import { getTreeColor } from "../../../../utils/views/applications";
import { formatBytes } from "../../../../utils/format";
import { colors } from "../../../../utils/colors";
import { EXPORT_CSV_LABELS, routeURLs } from "../../../../utils/enums";
import { removeLocalStorageFlag, downloadAsCSVFile } from "../../../../utils/common";
import { createThemeChangeObserver } from "../../../../common/Theme";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
const [HbrButton, HbrIcon, HbrTooltip] = reactWrapper(["hbr-button", "hbr-icon", "hbr-tooltip"]);

// Factory function to create tree map configuration with translations
const createTreeMapConfig = (tViews) => {
  const treeMapData = [
    {
      name: "Good",
      children: []
    },
    {
      name: "Fair",
      children: []
    },
    {
      name: "Poor",
      children: []
    },
    {
      name: "Unknown (No QoE)",
      children: []
    }
  ];

  const getHtmlTemplate = () => `
    <div>
      <table>
        <tr>
          <td class="tooltip-header-tree-map">{tooltip}</td>
          <td></td>
        </tr>
        <tr class="tooltip-tree-map-first-row">
          <td>${tViews('appExperienceQoE')}</td>
          <td><b><span style="color:{color};">{vqoeScore}</span></b>{vqoeScoreText}</td>
        </tr>
        <tr class="tooltip-tree-map">
          <td>{siteText}</td>
          <td>
            {totalSites}${tViews('appExperienceCountUnit')}({goodCount}${tViews('appExperienceCountUnit')}${tViews('appExperienceGood')}${tViews('appExperienceCountSep')}{fairCount}${tViews('appExperienceCountUnit')}${tViews('appExperienceFair')}${tViews('appExperienceCountSep')}{badCount}${tViews('appExperienceCountUnit')}${tViews('appExperiencePoor')}${tViews('appExperienceCountSep')}{NoQoECount}${tViews('appExperienceCountUnit')}${tViews('appExperienceGray')})
          </td>
        </tr>
        <tr class="tooltip-tree-map">
          <td>${tViews('appExperienceUsage')}</td>
          <td>{usageConverted} ({usagePercentage}% ${tViews('appExperienceOut')})</td>
        </tr>
        <tr class="tooltip-tree-map">
          <td>${tViews('appExperiencePacket')}</td>
          <td>{packetLoss}{packetLossUnits}</td>
        </tr>
        <tr class="tooltip-tree-map">
          <td>${tViews('appExperienceLatency')}</td>
          <td>{latency}{latencyUnits} </td>
        </tr>
        <tr class="tooltip-tree-map">
          <td>${tViews('appExperienceJitter')}</td>
          <td>{jitter}{jitterUnits}</td>
        </tr>
      </table>
    </div>`;

  const createTreeMapObject = (d, selectedSite, appDataTopUsage) => {
    let tips = "";

    if (d.application.toLowerCase() === "unknown") {
      if (d.application_family_long_name.toLowerCase() == "unknown") {
        tips = `${d.application} (${d.application_class})`;
      } else {
        tips = `${d.application} (${d.application_family_long_name})`;
      }
    } else {
      tips = d.application;
    }

    const usagePercentage = appDataTopUsage ? (d.usage / appDataTopUsage) * 100 : 0;
    const usage = usagePercentage < 1 ? appDataTopUsage / 100 : d.usage;
    
    return {
      name: d.application,
      siteText: selectedSite == -1 ? tViews('appExperienceSite') : tViews('appExperienceCircuit'),
      tooltip: tips,
      color: getTreeColor(d.vqoe_score),
      usage: usage,
      usageConverted: formatBytes(d.usage),
      usagePercentage: d.usage_percentage < 1 ? "<1" : d.usage_percentage,
      packetLoss: d.packet_loss,
      packetLossUnits: d.packet_loss == "-" ? "" : " %",
      jitter: d.jitter,
      jitterUnits: d.jitter == "-" ? "" : " ms",
      latency: d.latency,
      latencyUnits: d.latency == "-" ? "" : " ms",
      vqoeScore: d.vqoe_score,
      vqoeScoreText: d.vqoe_score == "-" ? "" : tViews('appExperienceOutOf10'),
      totalSites: d.usage_site_count,
      fairCount: d.fair_count,
      goodCount: d.good_count,
      badCount: d.bad_count ? d.bad_count : 0,
      NoQoECount: d.gray_count ? d.gray_count : 0
    };
  };

  return {
    treeMapData,
    getHtmlTemplate,
    createTreeMapObject
  };
};

const TreeMap = ({ data, selectedSite, resetFunc, reporting, appDataTopUsage }) => {
  const { tViews } = useI18n('views');
  const { treeMapData, getHtmlTemplate, createTreeMapObject } = createTreeMapConfig(tViews);
  const chartRef = useRef(null);
  const CHART_ID = "app_top20_usage";
  const navigate = useNavigate();
  const [chartData, setChartData] = useState();
  useEffect(() => {
    if (!chartRef.current) {
      /* Create chart */
      am4core.options.autoDispose = true;
      chartRef.current = am4core.create(CHART_ID, am4charts.TreeMap);

      data.map(d => {
        const obj = createTreeMapObject(d, selectedSite, appDataTopUsage);
        treeMapData[0].children.push(obj);
      });

      /* Show color based on fair, poor and good */
      chartRef.current.data = treeMapData;

      /* Set color step */
      chartRef.current.colors.step = 2;

      /* Define data fields */
      chartRef.current.dataFields.value = "usage";
      chartRef.current.dataFields.name = "name";
      chartRef.current.dataFields.color = "color";
      chartRef.current.dataFields.children = "children";
      chartRef.current.layoutAlgorithm = chartRef.current.binaryTree;
      chartRef.current.zoomable = false;
      chartRef.current.numberFormatter.numberFormat = "#.#";

      const topContainer = chartRef.current.chartContainer.createChild(
        am4core.Container
      );
      topContainer.toBack();
      topContainer.paddingBottom = 15;
      topContainer.width = am4core.percent(100);

      const dateTitle = topContainer.createChild(am4core.Label);
      dateTitle.userClassName = "app-experince-usage";
      dateTitle.marginTop = "-8px";
      dateTitle.text = `${tViews('appExperienceByUsage')}`;
      dateTitle.align = "left";
      dateTitle.fontWeight = 700;
      dateTitle.fontSize = 16;
      dateTitle.fontFamily = "Sharp Sans";
      
      // Function to update title color from CSS variable
      const updateTitleColor = () => {
        const cssVarColor = getComputedStyle(document.documentElement)
          .getPropertyValue("--hbr-color-neutral-text-medium").trim() || "#373c42";
        dateTitle.fill = am4core.color(cssVarColor);
      };
      updateTitleColor();
      
      // Setup theme observer for dynamic updates
      const titleThemeObserver = createThemeChangeObserver(updateTitleColor);
      
      dateTitle.element.attr({
        role: "heading",
        "aria-level" : "3"
      })

      /* level 1 series template */
      const level1SeriesTemplate = chartRef.current.seriesTemplates.create("1");
      level1SeriesTemplate.cursorOverStyle = am4core.MouseCursorStyle.pointer;

      level1SeriesTemplate.tooltip.getFillFromObject = false;
      level1SeriesTemplate.tooltip.autoTextColor = false;
      level1SeriesTemplate.tooltip.animationDuration = 0;
      level1SeriesTemplate.tooltip.pointerOrientation = "right";
      level1SeriesTemplate.strokeOpacity = 1;

      level1SeriesTemplate.tooltip.background.fill = am4core.color(
        colors.gray100
      );
      level1SeriesTemplate.tooltip.label.fill = am4core.color(colors.gray40);
      // set tooltip border style
      level1SeriesTemplate.tooltip.background.stroke = am4core.color(
        colors.gray45
      );
      level1SeriesTemplate.tooltip.background.strokeWidth = 2;
      // remove tooltip pointer
      // set tooltip shadow style
      const shadow = level1SeriesTemplate.tooltip.background.filters.getIndex(
        0
      );
      shadow.dy = 4;
      shadow.blur = 12;
      shadow.color = am4core.color(colors.gray0);
      shadow.opacity = 0.18;

      const level1ColumnTemplate = level1SeriesTemplate.columns.template;
      const htmlTemplate = getHtmlTemplate();
      level1ColumnTemplate.tooltipHTML = htmlTemplate;
       level1ColumnTemplate.hoverOnFocus = true;
      level1SeriesTemplate.tooltip.keepTargetHover = true;
      level1ColumnTemplate.readerDescription = `
  {tooltip}. QoE score: {vqoeScore} out of 10. 
  {siteText}: {totalSites} sites 
  (Good: {goodCount}, Fair: {fairCount}, Poor: {badCount}, No QoE: {NoQoECount}). 
  Usage: {usageConverted} ({usagePercentage}%). 
  Packet Loss: {packetLoss}{packetLossUnits}. 
  Latency: {latency}{latencyUnits}. 
  Jitter: {jitter}{jitterUnits}.
`;
      level1ColumnTemplate.column.fillOpacity = 1;
      level1ColumnTemplate.column.strokeWidth = 4;
      level1ColumnTemplate.column.stroke = am4core.color(colors.gray100);

      /* Add labels */
      const bullet1 = level1SeriesTemplate.bullets.push(
        new am4charts.LabelBullet()
      );
      bullet1.locationY = 0.5;
      bullet1.locationX = 0.5;
      bullet1.label.text =
        "[font-size:14px;font-weight:600;]{usagePercentage}% [font-size:12px;font-weight:400;]{name}";
      bullet1.label.cursorOverStyle = am4core.MouseCursorStyle.pointer;
      bullet1.fontWeight = 300;
      bullet1.label.fill = am4core.color(colors.gray15);
      bullet1.fill = am4core.color(colors.gray100);
      bullet1.tooltipHTML = htmlTemplate;

      bullet1.events.on("hit", ev => {
        removeLocalStorageFlag("vAnalytics_app360Tab");
        resetFunc();
        const { name } = ev.target.dataItem.dataContext;
        const path = generatePath(routeURLs.app360, { appName: name });
        navigate(path);
      });
      /** Add event to check the height and width of the map shapes */
      /*** when browser is resized ,application name in the tree map is hide .so we commented this*/
      /* level1SeriesTemplate.columns.template.events.on("sizechanged", ev => {
        if (ev.target.dataItem && ev.target.dataItem.bullets) {
          const width = ev.target.pixelWidth;
          const height = ev.target.pixelHeight;

          ev.target.dataItem.bullets.each((id, bullet) => {
            if (height > 60 && width > 60) {
              bullet.show();
            } else {
              bullet.hide();
            }
          });
        }
      }); */

      /** Add event to tigger redirect to page 360 */
      level1SeriesTemplate.columns.template.interactionsEnabled = true;
      level1SeriesTemplate.columns.template.events.on("hit", ev => {
        removeLocalStorageFlag("vAnalytics_app360Tab");
        resetFunc();
        const { name } = ev.target.dataItem.dataContext;
        const path = generatePath(routeURLs.app360, { appName: name });
        navigate(path);
      });
      chartRef.current.maxLevels = 2;
       // set chart data to export csv data 
     setChartData(chartRef);    
      return () => {
        titleThemeObserver.disconnect();
        if (chartRef.current) chartRef.current.dispose();
      };
    }
    return () => {
      if (chartRef.current) {
        setTimeout(
          ref => {
            ref.dispose();
            chartRef.current.events.disable();
          },
          100,
          chartRef.current
        );
      }
    };
  }, []);

  const onDownloadCsvClick = () => {
    if (chartData) { 
    const data = chartData.current.data[0].children;      
     // Fields to exclude
    const exclude = new Set(["siteText", "tooltip", "color", "usage", "packetLossUnits", "jitterUnits", "latencyUnits", "vqoeScoreText"]); 
      // Extract headers dynamically from keys of first object
    const rawHeaders = Object.keys(data[0]).filter(h => !exclude.has(h));
    // Build rows
     // Build rows with custom formatting
    const orderedCounts = ["goodCount", "fairCount", "badCount", "NoQoECount"];     
    const headers = [
    ...rawHeaders.filter(h => !orderedCounts.includes(h)),      // keep rest as-is
    ...orderedCounts.filter(c => rawHeaders.includes(c)),      // place counts in order
    ]; 
    const rows = data.map(obj =>
      headers.map(h => {
        if (h === "packetLoss") {
          const val = obj[h];
          return (val === "-" || val == null) ? "0%" : `${val}%`;
        }
        if (h === "usagePercentage") {
          const val = obj[h];
          if (val === "-" || val == null) {
            return "0%";
          } 
          if (val === "<1") {
            return "<1%";  // keep it as-is
          }
          const num = Number(val);
          return `${num.toFixed(1)}%`;  // format to 1 decimal
        }
        if (h === "jitter" || h === "latency") {
          const val = obj[h];
          return (val === "-" || val == null) ? "0 ms" : `${val} ms`;
        }
        return obj[h];
      })
    );  
    // Rename headers for CSV display
    const headerDisplay = {
    NoQoECount: "Unknown(NoQoE)Count"
    };
    const csvHeaders = headers.map(h => headerDisplay[h] || h);
    // Build CSV string
    const csvContent = [csvHeaders, ...rows]
      .map(row =>
        row
          .map(value =>
            typeof value === "string" && value.includes(",")
              ? `"${value}"` // wrap in quotes if value contains a comma
              : value
          )
          .join(",")
      )
      .join("\n"); 
      // Create CSV content
      downloadAsCSVFile(csvContent, EXPORT_CSV_LABELS.APPLICATIONS_TOP20_USAGE);
    }
  };
 

  return (
      <>
      {reporting !== true && (
      <div className="application-tree-map-download">
        <HbrTooltip content={tViews('downloadCSV')}>
          <HbrButton 
          size="medium" 
          variant="text" 
          data-testid="DownloadCsvBtn" 
          onClick={() => onDownloadCsvClick()} 
          arialabel={tViews('a11yLabels.csvDownloadButton') + tViews('a11yLabels.appExperienceTop20Usage')}
          >
             <HbrIcon name="file-csv" label={tViews('downloadCSV')} />
          </HbrButton>
        </HbrTooltip> 
      </div>
      )}
      <div id={CHART_ID} className="app-tree-map" />
      </>
    );
};

TreeMap.propTypes = {
  data: PropTypes.array.isRequired,
  selectedSite: PropTypes.number.isRequired,
  reporting: PropTypes.bool.isRequired,
  resetFunc: PropTypes.func.isRequired,
  appDataTopUsage: PropTypes.number
};

export default TreeMap;
